import {KeyboardEvent, RefObject, useEffect, useRef} from 'react';
import {
  QuestionSourceMenu as Menu,
  QuestionSourceMenuTitle,
  QuestionSourceOption,
  QuestionSourceOptionDescription,
  QuestionSourceOptionTitle,
} from './content-step.styles';
import {QuestionSource} from './types';

interface QuestionSourceMenuProps {
  anchorRef: RefObject<HTMLDivElement>;
  open: boolean;
  onClose: () => void;
  onSelect: (source: QuestionSource) => void;
}

export const QuestionSourceMenu = ({
  anchorRef,
  open,
  onClose,
  onSelect,
}: QuestionSourceMenuProps) => {
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    optionRefs.current[0]?.focus();

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [anchorRef, onClose, open]);

  if (!open) return null;

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const lastIndex = optionRefs.current.length - 1;
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
        ? lastIndex
        : event.key === 'ArrowDown'
        ? (index + 1) % optionRefs.current.length
        : (index - 1 + optionRefs.current.length) %
          optionRefs.current.length;
    optionRefs.current[nextIndex]?.focus();
  };

  const options: Array<{
    source: QuestionSource;
    title: string;
    description: string;
  }> = [
    {
      source: 'template',
      title: 'Question library',
      description: 'Choose from reusable questions organised by category',
    },
    {
      source: 'custom',
      title: 'Custom question',
      description: 'Write a question from scratch',
    },
  ];

  return (
    <Menu role="menu" aria-label="Question source">
      <QuestionSourceMenuTitle>
        How do you want to add a question?
      </QuestionSourceMenuTitle>
      {options.map((option, index) => (
        <QuestionSourceOption
          key={option.source}
          ref={(element) => {
            optionRefs.current[index] = element;
          }}
          type="button"
          role="menuitem"
          onKeyDown={(event) => handleKeyDown(event, index)}
          onClick={() => onSelect(option.source)}>
          <QuestionSourceOptionTitle>
            {option.title}
          </QuestionSourceOptionTitle>
          <QuestionSourceOptionDescription>
            {option.description}
          </QuestionSourceOptionDescription>
        </QuestionSourceOption>
      ))}
    </Menu>
  );
};
